import type { StyleProp, ViewStyle } from "react-native";
import { View } from "react-native";
import { useTranslation } from "react-i18next";

import type { UiTheme } from "../theme";
import { Icon } from "../Icon/Icon";
import { IconButton } from "../primitives/IconButton";
import { RowAction } from "../primitives/RowAction";
import type { LibraryItem } from "./useLibrary";

// The actions row of LibraryItemCard, split out to keep that file under the
// 500-line cap (it was 424 and every row added here pushed it further). Pure
// move: same elements, same order, same props.
//
// Every handler is optional, and its presence IS the gate — a host without a
// capability (no logger on web → no Avvia) simply doesn't pass it, and the
// row disappears. That convention is why this component takes handlers rather
// than a list of enabled action names.
//
// `styles.actions` stays owned by the parent and arrives as `style`: it
// encodes a gap tuned against the primitives' hitSlop, and a second
// definition here could drift from it silently.
//
// ⚠️ That inverts the convention the primitives in ../primitives use, where
// the component owns a base style and `style` is only a caller's override.
// It is deliberate while this has exactly ONE caller: there is no base to
// own that the parent isn't already defining. If a second caller appears,
// flip it — move `actions` into a makeStyles here (the TierOneTrendRow
// shape) and delete it from LibraryItemCard's makeStyles — rather than
// letting the second caller hand in its own copy of the gap.
export type LibraryItemCardActionsProps = {
  theme: UiTheme;
  item: LibraryItem;
  /** Row name, spoken by the pencil/trash hints that have no visible label. */
  name: string;
  style?: StyleProp<ViewStyle>;

  /** Inline preview toggle state — drives both the label and the chevron. */
  open: boolean;
  onTogglePreview: () => void;
  previewLabel: string;

  /** Personal plans on hosts with a logger (mobile) — starts the session. */
  onStart?: () => void;
  starting?: boolean;

  onFollow?: () => void;
  following?: boolean;

  onPublish?: () => void;
  onUnfollow?: () => void;
  /** True while a publish/unfollow write is in flight. */
  statusBusy?: boolean;

  /** Already carries the cloning / already-cloned state as text. On a
   *  Metria program this row is the Follow gesture, so the label is
   *  the follow wording — the caller decides, this component doesn't. */
  cloneLabel: string;
  /** Spoken hint for that row. Optional: only the follow wording needs
   *  to say where the program lands. */
  cloneHint?: string;
  onClone: (item: LibraryItem) => void;
  cloning: boolean;
  cloneBusy: boolean;
  alreadyCloned: boolean;

  /** #354: the caller's own program only; opens SaveAsModelDialog. */
  onSaveAsModel?: () => void;

  onEdit?: () => void;

  /** CO-21 — presence gates the icon (coach view + own row, see
   *  buildLibraryItemCardProps' guard); the smoke gap this closes was
   *  "assigning only works from inside the editor". */
  onAssign?: () => void;

  /** Presence gates the trash; the press itself goes through onDeletePress. */
  onDelete?: () => void;
  /**
   * Kept in the parent on purpose: it closes over the armed-confirm state and
   * the host/inline seam. Rebuilding it here from a passed-down `confirmDelete`
   * would reintroduce the stale-`confirming` bug it exists to avoid.
   */
  onDeletePress: () => void;
  /** Armed state — read directly, since it picks WHICH element renders. */
  confirming: boolean;
  /** This row is FOLLOWED (LibraryItemCard's own prop): the delete slot is
   *  "stop following" and deletes the caller's pointer, never the author's
   *  program. Only the glyph and the spoken name change here. The gating
   *  happened in buildLibraryItemCardProps. */
  readOnly?: boolean;
};

/** The spoken name of an action: its label, a dash, the row it belongs to. */
export const spokenLabel = (label: string, name: string) => `${label} \u2014 ${name}`;

export function LibraryItemCardActions({
  theme,
  item,
  name,
  style,
  open,
  onTogglePreview,
  previewLabel,
  onStart,
  starting = false,
  onFollow,
  following = false,
  onPublish,
  onUnfollow,
  statusBusy = false,
  cloneLabel,
  cloneHint,
  onClone,
  cloning,
  cloneBusy,
  alreadyCloned,
  onSaveAsModel,
  onEdit,
  onAssign,
  onDelete,
  onDeletePress,
  confirming,
  readOnly = false,
}: LibraryItemCardActionsProps) {
  const { t } = useTranslation();
  // ⭐ Found by the QA bench, 30/08: FIVE buttons announced "Follow" and it
  // could not tell them apart — neither can a screen reader in a list of five
  // programs, which is the whole reason this row is driven by its announced
  // name. The visible labels stay one word (the row has no space for more) and
  // the spoken one adds its program. Built from the LABEL, not from a fixed
  // verb, so the state a label carries ("Following…", "Already yours") is
  // spoken too instead of being dropped.
  const spoken = (label: string) => spokenLabel(label, name);
  const saveAsModelLabel = t("library.saveAsModel", { defaultValue: "Save as model" });

  return (
    <View style={style}>
      <RowAction
        theme={theme}
        label={previewLabel}
        onPress={onTogglePreview}
        expanded={open}
        trailing={
          <Icon
            name={open ? "chevron-up" : "chevron-down"}
            size={theme.typography.sizes.sm}
            color={theme.palette.ink2}
          />
        }
      />
      {onStart ? (
        <RowAction
          theme={theme}
          label={t("plans.start", { defaultValue: "Start" })}
          onPress={onStart}
          disabled={starting}
          accessibilityLabel={t("mobile.plan.startPlanA11y", {
            name,
            defaultValue: "Start {{name}}",
          })}
        />
      ) : null}
      {onFollow ? (
        <RowAction
          theme={theme}
          label={
            following
              ? t("library.following", { defaultValue: "Following…" })
              : t("program.follow", { defaultValue: "Follow" })
          }
          onPress={onFollow}
          disabled={following}
          accessibilityLabel={spoken(
            following
              ? t("library.following", { defaultValue: "Following…" })
              : t("program.follow", { defaultValue: "Follow" }),
          )}
          accessibilityHint={t("program.followHint", {
            defaultValue: "Follow this program (it shows up in In progress)",
          })}
        />
      ) : null}
      {onPublish ? (
        <RowAction
          theme={theme}
          label={t("program.publish", { defaultValue: "Publish" })}
          onPress={onPublish}
          disabled={statusBusy}
          accessibilityLabel={spoken(
            t("program.publish", { defaultValue: "Publish" }),
          )}
          accessibilityHint={t("program.publishHint", {
            defaultValue: "Make this program available to follow",
          })}
        />
      ) : null}
      {onUnfollow ? (
        <RowAction
          theme={theme}
          label={t("program.unfollow", { defaultValue: "Unfollow" })}
          onPress={onUnfollow}
          disabled={statusBusy}
          accessibilityLabel={spoken(
            t("program.unfollow", { defaultValue: "Unfollow" }),
          )}
          accessibilityHint={t("mobile.plan.programUnfollowHint", {
            defaultValue: "Stop following this program (it stays in My programs)",
          })}
        />
      ) : null}
      <RowAction
        theme={theme}
        label={cloneLabel}
        onPress={() => onClone(item)}
        // ⭐ The QA bench found FIVE buttons all announced "Follow" and could
        // not tell them apart; neither can a screen reader in a list of five
        // programs. The visible label stays one word — the row has no space
        // for more — and the spoken one adds which row it belongs to, the
        // same composition the pencil and the trash below already use. It is
        // built from `cloneLabel` rather than from a fixed verb so the state
        // the label carries ("Already yours") is spoken too.
        accessibilityLabel={spoken(cloneLabel)}
        accessibilityHint={cloneHint}
        // Busy/cloned state is never colour-alone: it lands in a11y
        // state and, for cloning/already-cloned, in the label itself.
        disabled={cloning || cloneBusy || alreadyCloned}
      />
      {onSaveAsModel ? (
        <RowAction
          theme={theme}
          label={saveAsModelLabel}
          onPress={onSaveAsModel}
          accessibilityLabel={spoken(saveAsModelLabel)}
          accessibilityHint={t("library.saveAsModelHint", {
            defaultValue: "Reuse its days in New program. Loads become a % of your MAX.",
          })}
        />
      ) : null}
      {/* Edit/Delete are icons (pencil/trash — Antenore 2026-07-04, HR-7):
          with up to 5 actions the all-text row barely fit a large phone.
          The semantic/state-bearing actions above stay text (their labels
          carry state — "Cloning…" — which an icon would reduce to
          opacity-alone); these two are universally glyphable. CO-21's Assign
          (the roster glyph, coach-view-only) joins them for the same reason
          — a sixth text button had no row left to fit into. */}
      {onEdit ? (
        <IconButton
          theme={theme}
          name="edit"
          onPress={onEdit}
          // The name disambiguates WHICH row's pencil this is — with no
          // visible text label left, a screen reader in a long list has no
          // other anchor (HR-7 a11y review). It now rides in the LABEL, like
          // every other row here; the hint keeps only what the label does not
          // already say, and a plan has nothing left to add.
          accessibilityLabel={spoken(
            t("program.edit", { defaultValue: "Edit" }),
          )}
          accessibilityHint={
            item.kind === "program"
              ? t("program.editing", { name })
              : undefined
          }
        />
      ) : null}
      {onAssign ? (
        <IconButton
          theme={theme}
          name="users"
          onPress={onAssign}
          // Same disambiguation as the pencil — no visible label to anchor a
          // screen reader in a long list, so the name rides in the spoken one.
          // ⛔ The namespace follows the ITEM, like AssignSheet's own `tk`
          // does: today program.assign and plans.assign read the same in all
          // five locales, but a language that inflects the verb by object
          // would drift here and nowhere else, silently. Found by
          // consolidation-reviewer, 20/09.
          accessibilityLabel={spoken(
            t(item.kind === "plan" ? "plans.assign" : "program.assign", {
              defaultValue: "Assign",
            }),
          )}
        />
      ) : null}
      {onDelete ? (
        confirming ? (
          // The inline confirm step stays a destructive TEXT action — the
          // irreversible tap must never be icon-only.
          <RowAction
            theme={theme}
            label={t("program.deleteConfirm", { defaultValue: "Confirm?" })}
            onPress={onDeletePress}
            accessibilityLabel={spoken(
              t("program.deleteConfirm", { defaultValue: "Confirm?" }),
            )}
            // Red is for the tap that destroys. An unfollow does not, so
            // this step is the plain label on a followed row.
            destructive={!readOnly}
          />
        ) : readOnly ? (
          // A trash can here would be a lie: stopping to follow removes the
          // caller's own pointer and leaves the author's program untouched
          // (next/coaching.md, "Smetti di seguire"). So: the neutral box and
          // an ✕, not the destructive trash.
          <IconButton
            theme={theme}
            name="x"
            onPress={onDeletePress}
            accessibilityLabel={spoken(
              t("library.unfollow", { defaultValue: "Stop following" }),
            )}
            accessibilityHint={t("library.unfollowHint", {
              defaultValue:
                "Remove it from your library. Its author keeps it.",
            })}
          />
        ) : (
          <IconButton
            theme={theme}
            name="trash"
            variant="destructive"
            onPress={onDeletePress}
            // Same disambiguation as the pencil, and for the same reason:
            // the icon carries no visible text, so its spoken name has to
            // say which row it would delete.
            accessibilityLabel={spoken(
              t("workout.delete", { defaultValue: "Delete" }),
            )}
          />
        )
      ) : null}
    </View>
  );
}
